/**
 * Cluster of grass blades that, when positioned at a card's bottom edge with
 * z-index above the card, looks like blades growing UP through the card panel.
 * Bases sit at the bottom of the viewBox so the blades emerge cleanly when
 * the SVG bottom aligns with the card edge. Mirror via the `side` prop so the
 * left and right corners feel like one composition rather than two duplicates.
 *
 * The `layer` prop renders alternating subsets so a back-layer (z-0, behind
 * the card) and front-layer (z-20, in front) can be rendered together. Half
 * the blades sit behind the card and half in front — the card weaves through
 * them, reading as "intertwined" rather than just stacked on top.
 */
interface Blade {
  d: string
  width: number
  type: "light" | "dark"
}

// Bases sit on a soft uneven "soil line" (varied y) rather than a perfect
// horizontal. Bases are also unevenly spaced along x to break the uniform
// fence-post look. Each cubic uses control points that bow in opposite
// directions for an organic S-shape, with mixed lean directions so blades
// don't all curl the same way. Tall, medium, and short blades interleave
// so the cluster reads as natural depth rather than a single height row.
// Front/back layering alternates by index, so even/odd blades are paired
// to keep the woven look balanced when split.
const BLADES: readonly Blade[] = [
  { d: "M 12 222 C 16 182, 8 134, 6 78", width: 3.0, type: "dark" },
  { d: "M 30 216 C 32 200, 38 180, 42 162", width: 1.9, type: "light" },
  { d: "M 52 220 C 48 188, 58 144, 54 108", width: 2.4, type: "light" },
  { d: "M 74 224 C 78 192, 66 142, 72 92", width: 2.8, type: "dark" },
  { d: "M 92 214 C 94 198, 100 178, 98 162", width: 2.0, type: "dark" },
  { d: "M 110 222 C 108 194, 120 152, 116 122", width: 2.6, type: "light" },
  { d: "M 132 218 C 138 188, 124 142, 130 96", width: 2.8, type: "light" },
  { d: "M 150 215 C 152 200, 160 182, 158 168", width: 1.8, type: "dark" },
  { d: "M 168 221 C 164 192, 176 150, 172 120", width: 2.4, type: "light" },
  { d: "M 188 219 C 192 192, 182 148, 186 102", width: 2.6, type: "dark" },
] as const

interface HeroGrassThroughProps {
  side: "left" | "right"
  layer?: "all" | "back" | "front"
}

export function HeroGrassThrough({ side, layer = "all" }: HeroGrassThroughProps) {
  const groupTransform = side === "right" ? "translate(200, 0) scale(-1, 1)" : ""

  const blades =
    layer === "back"
      ? BLADES.filter((_, i) => i % 2 === 0)
      : layer === "front"
      ? BLADES.filter((_, i) => i % 2 === 1)
      : BLADES

  // Suffix the gradient IDs by layer so the back/front instances don't share
  // <defs> ids in the same DOM (would otherwise collide and re-color).
  const idSuffix = `${side}-${layer}`

  return (
    <svg
      viewBox="0 0 200 220"
      role="presentation"
      aria-hidden="true"
      className="w-full h-auto"
    >
      <defs>
        <linearGradient id={`hgt-light-${idSuffix}`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#16a34a" />
          <stop offset="100%" stopColor="#86efac" />
        </linearGradient>
        <linearGradient id={`hgt-dark-${idSuffix}`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#14532d" />
          <stop offset="100%" stopColor="#22c55e" />
        </linearGradient>
      </defs>

      <g transform={groupTransform} strokeLinecap="round" fill="none">
        {blades.map((blade, i) => (
          <path
            key={i}
            d={blade.d}
            stroke={`url(#hgt-${blade.type}-${idSuffix})`}
            strokeWidth={blade.width}
          />
        ))}
      </g>
    </svg>
  )
}
